import React from 'react'
import {
    getMediaPlaybackState,
    setMediaPlaybackState,
    subscribeToMediaPlaybackState,
} from 'state/mediaPlaybackCoordinator'

const positive = value => (Number(value) > 0 ? Number(value) : null)

// A song's time on a card that only plays it through the dock: where the dock
// is in it and how long it is. The dock's reading wins once it has one; before
// that the length is the payload's and the position is the start.
export function radioSongTiming(snapshot, knownDuration) {
    const duration = positive(snapshot?.duration) ?? positive(knownDuration)
    const current = positive(snapshot?.currentTime) ?? 0
    const position = duration ? Math.min(current, duration) : current
    return {
        position,
        duration,
        progress: duration ? position / duration : null,
    }
}

// Reads a file's length from a detached, muted loader that never plays and is
// dropped as soon as it reports; the length lands in playback state. Returns
// the cancel.
export function probeMediaDuration(mediaKey, src) {
    if (typeof Audio === 'undefined') return undefined

    let cancelled = false
    const probe = new Audio()
    const release = () => {
        probe.removeEventListener('loadedmetadata', finish)
        probe.removeEventListener('error', finish)
        try {
            probe.src = ''
        } catch {}
    }
    function finish() {
        if (cancelled) return
        cancelled = true
        const measured = probe.duration
        release()
        if (Number.isFinite(measured) && measured > 0) {
            setMediaPlaybackState(mediaKey, { duration: measured })
        }
    }

    probe.preload = 'metadata'
    probe.muted = true
    probe.addEventListener('loadedmetadata', finish)
    probe.addEventListener('error', finish)
    probe.src = src

    return () => {
        cancelled = true
        release()
    }
}

// Follows the dock's playback state for the song. A video song's payload
// carries no length, so until the dock has played it the length comes from a
// one-off metadata read — the same detached probe the hosted audio core uses —
// cached in playback state, where the dock and every card of the song find it.
export function useRadioSongTiming(mediaKey, src, knownDuration) {
    const [snapshot, setSnapshot] = React.useState(() =>
        mediaKey ? getMediaPlaybackState(mediaKey) : null,
    )

    React.useEffect(() => {
        if (!mediaKey) return undefined
        const sync = () => setSnapshot(getMediaPlaybackState(mediaKey))
        sync()
        return subscribeToMediaPlaybackState(mediaKey, sync)
    }, [mediaKey])

    const hasDuration = Boolean(positive(snapshot?.duration) || positive(knownDuration))

    React.useEffect(() => {
        if (!mediaKey || !src || hasDuration) return undefined
        return probeMediaDuration(mediaKey, src)
    }, [hasDuration, mediaKey, src])

    return radioSongTiming(snapshot, knownDuration)
}
